import { ASSET_TYPES } from "helpers/assets/constants"
import { combineTanStackStatuses, DataStatus } from "helpers/data-statuses"
import { IsoDate } from "helpers/types/dates"
import { PositionSetEquity, PositionSetOtherAsset, SelectedInstrument, SelectedTypedInstrument } from "helpers/types/instruments/selected"
import { compact, union } from "lodash"
import { useMemo } from "react"
import { useFilterSecurities } from "resources/filters"

interface FiltersSecurityData {
  modelProviderIds: string[]
  swapTickers: string[]
}

export default function useFilterSecuritiesData(
  modelId: string | null,
  account: string,
  instrument: SelectedTypedInstrument,
  secondInstrument: SelectedInstrument | undefined,
  comparisonInstrument: SelectedInstrument | undefined,
  currentDate: IsoDate,
  isExpanded: boolean,
): [FiltersSecurityData, DataStatus] {
  const [instrumentFilterSecurities, instrumentFilterSecuritiesStatus] = useFilterSecurities(modelId, account, instrument, currentDate, currentDate, isExpanded)
  const [secondInstrumentFilterSecurities, secondInstrumentFilterSecuritiesStatus] = useFilterSecurities(modelId, account, secondInstrument, currentDate, currentDate, isExpanded)
  const [comparisonInstrumentFilterSecurities, comparisonInstrumentFilterSecuritiesStatus] = useFilterSecurities(modelId, account, comparisonInstrument, currentDate, currentDate, isExpanded)

  const filterSecurities = useMemo(() => {
    const instruments = compact([
      instrument && { filterInstrument: instrument, securities: instrumentFilterSecurities },
      secondInstrument && { filterInstrument: secondInstrument, securities: secondInstrumentFilterSecurities },
      comparisonInstrument && { filterInstrument: comparisonInstrument, securities: comparisonInstrumentFilterSecurities },
    ])

    return instruments.reduce((acc, securitiesInstrument) => {
      if (!securitiesInstrument) {
        return acc
      }

      const { filterInstrument, securities } = securitiesInstrument
      let modelProviderIds: string[] = []
      let swapTickers: string[] = []
      // If the instrument is an inline position set, we need to build out the model provider ids and swap tickers
      // [TODO] [APP-2206] We need to check for existence of dates because rebalance is calling with invalid instruments
      if (filterInstrument.type === undefined && filterInstrument.dates) {
        ASSET_TYPES.forEach((assetType) => {
          const assetTypeDate = filterInstrument.dates[0][assetType]
          if (!assetTypeDate) {
            return
          }

          switch (assetType) {
            case "indexes":
              acc.modelProviderIds.push(...(assetTypeDate as PositionSetOtherAsset[]).map((date) => `i.${date.id}`))
              break
            case "swaps":
              acc.swapTickers.push(...(assetTypeDate as PositionSetOtherAsset[]).map((date) => date.id))
              break
            case "equities":
              acc.modelProviderIds.push(...(assetTypeDate as PositionSetEquity[]).map(date => date.id.modelProviderId))
              break
            // [TODO] [APP-2197] We ignore other types (like currencies)
          }
        })
      } else if (securities) {
        modelProviderIds = securities.modelProviderIds
        swapTickers = securities.swapTickers
      }

      acc.modelProviderIds = union(acc.modelProviderIds, modelProviderIds)
      acc.swapTickers = union(acc.swapTickers, swapTickers)

      return acc
    }, { modelProviderIds: [], swapTickers: [] } as FiltersSecurityData)
  }, [instrumentFilterSecurities, secondInstrumentFilterSecurities, comparisonInstrumentFilterSecurities, instrument, secondInstrument, comparisonInstrument])

  return [
    filterSecurities,
    combineTanStackStatuses([
      instrumentFilterSecuritiesStatus,
      secondInstrumentFilterSecuritiesStatus,
      comparisonInstrumentFilterSecuritiesStatus,
    ]),
  ]
}
